import {
  Box,
  Grid,
  Typography,
  Chip,
  Button,
  Collapse,
  IconButton,
} from '@material-ui/core'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNotificationItemComponentStyle } from './notifications-item-component.styles'
import {
  AccessAlarm,
  ExpandMore,
  NotificationsOutlined,
  OpenInNew,
} from '@material-ui/icons'
import { UserNotification } from '../../../store/notifications/types'
import { useBundleNotificationsDrawerContext } from '../../../contexts/bundle-notifications-drawer.context'

/**
 * Converts ISO date string to American format (MM/DD/YYYY, HH:MM AM/PM)
 * @param isoDateString - ISO format date string (e.g., "2026-01-09T16:41:17.55")
 * @returns Formatted date string in American format
 */
const formatToAmericanDate = (isoDateString: string): string => {
  const date = new Date(isoDateString)
  // Format month and day with leading zeros (MM/DD)
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  const year = date.getFullYear()

  // Convert 24-hour format to 12-hour format with AM/PM
  let hours = date.getHours()
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  hours = hours % 12
  hours = hours ? hours : 12 // the hour '0' should be '12'
  const formattedHours = hours.toString().padStart(2, '0')

  return `${month}/${day}/${year}, ${formattedHours}:${minutes} ${ampm}`
}

/**
 * Notification item component that displays individual notification details
 * Supports bundle notifications with special click handling
 * @param props - UserNotification object containing notification data
 */
export const NotificationsItemComponent = (props: UserNotification) => {
  // Destructure notification properties
  const { subject, text, createdUtc, reviewed, markNotificationAsReviewed, orderLink } =
    props
  // Get styles and translation function
  const classes = useNotificationItemComponentStyle()
  const { t } = useTranslation()
  const { openDrawer } = useBundleNotificationsDrawerContext()
  const [expanded, setExpanded] = useState(false)

  // Check if this is a bundle notification by checking subject text
  const isBundleNotification = subject?.toLowerCase().includes('bundle')

  /**
   * Handles click event on notification item
   * Opens bundle drawer if notification is a bundle type
   */
  const handleClick = () => {
    if (isBundleNotification) {
      openDrawer()
    }
  }

  const handleToggleExpanded = () => {
    setExpanded((current) => !current)
  }

  return (
    <Box
      className={
        isBundleNotification
          ? `${classes.container} ${classes.bundleClickable}`
          : classes.container
      }
      onClick={isBundleNotification ? handleClick : undefined}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} container className={classes.notificationRow}>
          <Grid item className={classes.iconCell}>
            <NotificationsOutlined className={classes.notificationIcon} />
          </Grid>
          <Grid item className={classes.contentHolder}>
            {/* Header */}
            <div
              className={classes.summary}
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation()
                handleToggleExpanded()
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  handleToggleExpanded()
                }
              }}
            >
              <div className={classes.header}>
                <Typography variant="h6" className={classes.title} align="left">
                  {subject}
                </Typography>
                {/* Date and Time */}
                <div className={classes.dateHolder}>
                  <Chip
                    label={
                      reviewed
                        ? t('notifications.item.read')
                        : t('notifications.item.new')
                    }
                    color={reviewed ? 'default' : 'secondary'}
                    size="small"
                    className={classes.chip}
                  />
                  <span className={classes.date}>
                    <AccessAlarm fontSize="small" />
                    {formatToAmericanDate(createdUtc)}
                  </span>
                </div>
              </div>
              <IconButton
                size="small"
                className={`${classes.expandButton} ${
                  expanded ? classes.expandButtonOpen : ''
                }`}
                aria-expanded={expanded}
                aria-label={
                  expanded
                    ? t('notifications.item.collapse', 'Collapse notification')
                    : t('notifications.item.expand', 'Expand notification')
                }
              >
                <ExpandMore fontSize="small" />
              </IconButton>
            </div>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <div
                className={classes.details}
                onClick={(e) => {
                  e.stopPropagation()
                }}
              >
                {/* Notification text content */}
                {text && <p className={classes.text}>{text}</p>}
                {/* Order link if available */}
                {orderLink && (
                  <div className={classes.orderLinkContainer}>
                    <Button
                      variant="outlined"
                      size="small"
                      href={orderLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.orderLinkButton}
                      startIcon={<OpenInNew />}
                    >
                      {t('notifications.item.viewOrder', 'View Order')}
                    </Button>
                  </div>
                )}
                {/* Read status indicator or mark as read button */}
                <div>
                  {reviewed ? (
                    <span className={classes.readed}>
                      {t('notifications.item.readed')}
                    </span>
                  ) : (
                    <span
                      className={classes.markAsRead}
                      onClick={() => {
                        markNotificationAsReviewed?.()
                      }}
                    >
                      {t('notifications.item.markAsRead')}
                    </span>
                  )}
                </div>
              </div>
            </Collapse>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  )
}
